import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiLink2,
  FiImage as ImageIcon,
  FiCode,
} from "react-icons/fi";
import { LuHeading1, LuHeading2, LuHeading3 } from "react-icons/lu";
import { IoIosQuote } from "react-icons/io";
import { RiListOrdered } from "react-icons/ri";
import { MdUndo, MdRedo } from "react-icons/md";
import Dropdown from "react-bootstrap/Dropdown";

export default function EditorToolbar({
  editor,
  onInsertImage,
  onShowResources,
}) {
  const [textColor, setTextColor] = React.useState("#000000");
  const [highlightColor, setHighlightColor] = React.useState("#FFF34D");
  const [linkUrl, setLinkUrl] = React.useState("");
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const [_, setForceUpdate] = useState(0);

  useEffect(() => {
    if (!editor) return;

    const update = () => setForceUpdate((n) => n + 1); // force re-render

    editor.on("selectionUpdate", update);
    editor.on("transaction", update);

    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  const handleTextColor = (color) => {
    setTextColor(color);
    editor.chain().focus().setColor(color).run();
  };

  const handleHighlightColor = (color) => {
    setHighlightColor(color);
    editor.chain().focus().toggleHighlight({ color }).run();
  };

  const handleAddLink = () => {
    if (!linkUrl || !editor) return;

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: linkUrl, target: "_blank" })
      .run();

    setLinkUrl("");
    setShowLinkInput(false);
  };

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
  };

  const fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

  const selectionHasLink = () => {
    const { from, to } = editor.state.selection;

    let hasLink = false;

    editor.state.doc.nodesBetween(from, to, (node) => {
      if (node.marks.some((mark) => mark.type.name === "link")) {
        hasLink = true;
        return false; // stop iterating
      }
    });

    return hasLink;
  };

  const cursorInsideLink = () => {
    const { $from, empty } = editor.state.selection;

    if (!empty) return false; // ignore if text is selected

    // This checks if **any mark at the cursor position** is a link
    return $from.marks().some((mark) => mark.type.name === "link");
  };

  const showRemoveLinkButton = selectionHasLink() || cursorInsideLink();

  return (
    <div className="flex flex-wrap gap-2 p-3 bg-muted border border-border">
      {/* Undo/Redo */}
      <Button
        size="sm"
        variant={"outline-secondary"}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <MdUndo />
      </Button>
      <Button
        size="sm"
        variant={"outline-secondary"}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <MdRedo />
      </Button>

      <div className="h-6 border-l border-border" />

      {/* Text Formatting */}
      <Button
        size="sm"
        variant={editor.isActive("bold") ? "primary" : "outline-secondary"}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiBold />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive("italic") ? "primary" : "outline-secondary"}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiItalic />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive("underline") ? "primary" : "outline-secondary"}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Underline"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiUnderline />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive("code") ? "primary" : "outline-secondary"}
        onClick={() => editor.chain().focus().toggleCode().run()}
        title="Code"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiCode />
      </Button>

      <div className="h-6 border-l border-border" />

      {/* Headings */}
      {/* Headings */}
      <Button
        size="sm"
        variant={
          editor.isActive("heading", { level: 1 })
            ? "primary"
            : "outline-secondary"
        }
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 1, keepMarks: true })
            .run()
        }
        title="Heading 1"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <LuHeading1 />
      </Button>

      <Button
        size="sm"
        variant={
          editor.isActive("heading", { level: 2 })
            ? "primary"
            : "outline-secondary"
        }
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 2, keepMarks: true })
            .run()
        }
        title="Heading 2"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <LuHeading2 />
      </Button>

      <Button
        size="sm"
        variant={
          editor.isActive("heading", { level: 3 })
            ? "primary"
            : "outline-secondary"
        }
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 3, keepMarks: true })
            .run()
        }
        title="Heading 3"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <LuHeading3 />
      </Button>

      <div className="h-6 border-l border-border" />

      {/* Lists */}
      <Button
        size="sm"
        variant={
          editor.isActive("bulletList") ? "primary" : "outline-secondary"
        }
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet List"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiList />
      </Button>
      <Button
        size="sm"
        variant={
          editor.isActive("orderedList") ? "primary" : "outline-secondary"
        }
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Ordered List"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <RiListOrdered />
      </Button>

      <div className="h-6 border-l border-border" />

      {/* Alignment (text) */}
      <Button
        size="sm"
        variant={
          editor.isActive({ textAlign: "left" })
            ? "primary"
            : "outline-secondary"
        }
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        title="Align Left"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiAlignLeft />
      </Button>
      <Button
        size="sm"
        variant={
          editor.isActive({ textAlign: "center" })
            ? "primary"
            : "outline-secondary"
        }
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        title="Align Center"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiAlignCenter />
      </Button>
      <Button
        size="sm"
        variant={
          editor.isActive({ textAlign: "right" })
            ? "primary"
            : "outline-secondary"
        }
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        title="Align Right"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <FiAlignRight />
      </Button>

      <div className="h-6 border-l border-border" />

      {/* Font Size Dropdown */}
      <Dropdown className="d-inline">
        <Dropdown.Toggle
          variant="outline-secondary"
          size="sm"
          className="p-2 h-auto"
        >
          Size
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {fontSizes.map((size) => (
            <Dropdown.Item
              key={size}
              onClick={() => {
                editor.chain().focus().setFontSize(size).run();
              }}
            >
              {size}px
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {/* Text Color */}
      <div className="d-flex align-items-center gap-2">
        <input
          type="color"
          value={textColor}
          onChange={(e) => handleTextColor(e.target.value)}
          className="w-8 h-8 cursor-pointer rounded border border-border"
          title="Text Color"
          style={{ width: "32px", height: "32px" }}
        />
      </div>

      {/* Highlight Color */}
      <div className="d-flex align-items-center gap-2">
        <input
          type="color"
          value={highlightColor}
          onChange={(e) => handleHighlightColor(e.target.value)}
          className="w-8 h-8 cursor-pointer rounded border border-border"
          title="Highlight Color"
          style={{ width: "32px", height: "32px" }}
        />
      </div>

      <div className="h-6 border-l border-border" />

      {/* Quote */}
      {/* <Button
        size="sm"
        variant={
          editor.isActive("blockquote") ? "primary" : "outline-secondary"
        }
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Blockquote"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <IoIosQuote />
      </Button> */}

      <div className="h-6 border-l border-border" />

      {/* Image insert */}
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={onInsertImage}
        title="Insert Image"
        className="p-2 h-auto d-flex align-items-center justify-content-center"
      >
        <ImageIcon />
      </Button>

      <div className="h-6 border-l border-border" />

      {/* Link UI */}
      {showLinkInput ? (
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            className="px-2 py-1 text-sm border border-border rounded"
            style={{ width: "160px" }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddLink();
            }}
          />
          <Button
            size="sm"
            variant="primary"
            onClick={handleAddLink}
            className="p-2 h-auto"
          >
            Add
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl("");
            }}
            className="p-2 h-auto"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <Button
            size="sm"
            variant={editor.isActive("link") ? "primary" : "outline-secondary"}
            onClick={() => setShowLinkInput(true)}
            title="Add Link"
            className="p-2 h-auto d-flex align-items-center justify-content-center"
          >
            <FiLink2 />
          </Button>
          {showRemoveLinkButton && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleRemoveLink}
              title="Remove Link"
              className="p-2 h-auto"
            >
              Remove Link
            </Button>
          )}
        </>
      )}
      {/* <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => onShowResources(true)}
                title="Insert from Resources"
                className="p-2 h-auto d-flex align-items-center justify-content-center"
            >
                📁
            </Button> */}
    </div>
  );
}
